import { motion } from "framer-motion";
import { TECHNOLOGIES } from "@/lib/data";
import { SiSap, SiSnowflake } from "react-icons/si";
import { Cpu, LineChart, Zap, Layers, Cloud, Database, BarChart3, Server } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const TECH_META = {
  "Openlink Endur": { Icon: Cpu, color: "#D4AF37" },
  "Allegro": { Icon: LineChart, color: "#D4AF37" },
  "RightAngle": { Icon: Zap, color: "#D4AF37" },
  "AspectCTRM": { Icon: Layers, color: "#D4AF37" },
  "SAP": { Icon: SiSap, color: "#0FAAFF" },
  "Snowflake": { Icon: SiSnowflake, color: "#29B5E8" },
  "Azure": { Icon: Cloud, color: "#00A4EF" },
  "AWS": { Icon: Server, color: "#FF9900" },
  "Power BI": { Icon: BarChart3, color: "#F2C811" },
};

export default function Technologies() {
  useSEO({
    title: "Technology Expertise — Endur · Allegro · SAP · Snowflake · Azure · AWS | EnPrex",
    description: "The platforms EnPrex knows inside-out — Openlink Endur, Allegro, RightAngle, AspectCTRM, SAP, Snowflake, Databricks, Azure, AWS and Power BI.",
    path: "/technologies",
  });
  return (
    <div data-testid="technologies-page">
      <section className="container-x pt-24 pb-16 border-b border-white/5">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 font-mono">— Technologies</div>
          <h1 className="font-display text-5xl md:text-7xl font-light text-white tracking-tighter leading-[0.95] max-w-4xl">
            The <span className="italic">platforms</span> we know inside-out.
          </h1>
          <p className="mt-8 text-lg text-slate-300 max-w-2xl font-light leading-relaxed">
            Vendor-agnostic, deeply certified. Our practice leads are among the most senior implementers globally on each of these platforms.
          </p>
        </motion.div>
      </section>

      <section className="container-x py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TECHNOLOGIES.map((t, i) => {
            const meta = TECH_META[t.name] || { Icon: Database, color: "#D4AF37" };
            const Icon = meta.Icon;
            return (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
                className="border border-white/5 bg-white/[0.02] p-8 hover:border-white/20 hover:bg-white/[0.04] transition-all group"
                data-testid={`tech-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <Icon size={36} style={{ color: meta.color }} strokeWidth={1.2} />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    {t.category}
                  </span>
                </div>
                <h3 className="font-display text-2xl text-white font-medium">{t.name}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{t.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
